import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import RootLayout from './layouts/RootLayout'
import ErrorBoundary from './components/ErrorBoundary'
import LoadingSpinner from './components/LoadingSpinner'
import Home from './pages/Home'
import Creatives from './pages/Creatives'
import CreativeDetail from './pages/CreativeDetail'
import ClientDetail from './pages/ClientDetail'
import Services from './pages/Services'
import AllServices from './pages/AllServices'
import Events from './pages/Events'
import EventDetail from './pages/EventDetail'
import Cities from './pages/Cities'
import Countries from './pages/Countries'
import Booking from './pages/Booking'
import ServiceBookingWizard from './pages/booking/ServiceBookingWizard'
import QuickFikaBooking from './pages/booking/QuickFikaBooking'
import HowItWorks from './pages/HowItWorks'
import About from './pages/About'
import Contact from './pages/Contact'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import CreativesMap from './pages/CreativesMap'
import ServiceDetail from './pages/ServiceDetail'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import AddRole from './pages/AddRole'
import Dashboard from './pages/Dashboard'
import Unauthorized from './pages/Unauthorized'
import ProtectedRoute from './components/ProtectedRoute'
const Overview = lazy(() => import('./pages/dashboard/Overview'))
const ClientDashboard = lazy(() => import('./pages/dashboard/ClientDashboard'))
const MyBookings = lazy(() => import('./pages/dashboard/MyBookings'))
const Favorites = lazy(() => import('./pages/dashboard/Favorites')) // HIDDEN FOR LAUNCH
const Creative = lazy(() => import('./pages/dashboard/Creative'))
const Orders = lazy(() => import('./pages/dashboard/Orders'))
const Tasks = lazy(() => import('./pages/dashboard/Tasks'))
const Messages = lazy(() => import('./pages/dashboard/Messages'))
const Notifications = lazy(() => import('./pages/dashboard/Notifications'))
const Users = lazy(() => import('./pages/dashboard/Users'))
const Settings = lazy(() => import('./pages/dashboard/Settings'))
const Support = lazy(() => import('./pages/dashboard/Support'))
const CreativeDashboard = lazy(() => import('./pages/dashboard/CreativeDashboard'))
const MyServices = lazy(() => import('./pages/dashboard/MyServices'))
const ServiceRequests = lazy(() => import('./pages/dashboard/ServiceRequests'))
const Earnings = lazy(() => import('./pages/dashboard/Earnings'))
const PortfolioManager = lazy(() => import('./pages/dashboard/PortfolioManager'))
const UserProfile = lazy(() => import('./pages/dashboard/UserProfile'))
const CreativeProfile = lazy(() => import('./pages/dashboard/CreativeProfile'))
const Payments = lazy(() => import('./pages/dashboard/Payments'))
const MyEvents = lazy(() => import('./pages/dashboard/MyEvents'))
// Using Tailwind via index.css; no separate App.css needed

export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner label="Loading application..." />}>
        <Routes>
      <Route element={<RootLayout />}> 
        <Route path="/" element={<Home />} />
        <Route path="/creatives" element={<Creatives />} />
        <Route path="/creative/profile/:slug" element={<CreativeDetail />} />
        <Route path="/creatives/profile/:slug" element={<CreativeDetail />} />
        {/* Client/Buyer public profile routes */}
        <Route path="/client/profile/:slug" element={<ClientDetail />} />
        <Route path="/clients/profile/:slug" element={<ClientDetail />} />
        <Route path="/creatives/:slug" element={<CreativeDetail />} />
        <Route path="/creatives/:id" element={<CreativeDetail />} />
        <Route path="/creatives/map" element={<CreativesMap />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/allservices/*" element={<AllServices />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/services/:id" element={<ServiceDetail />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<EventDetail />} />
        {/* Cities routing: /country/:countrySlug/cities/:citySlug */}
        <Route path="/cities" element={<Cities />} />
        <Route path="/country/:countrySlug/cities" element={<Cities />} />
        <Route path="/country/:countrySlug/cities/:citySlug" element={<Cities />} />
        {/* Backward-compatibility (optional): keep /countries and /cities patterns if linked elsewhere */}
        <Route path="/countries/:countrySlug" element={<Cities />} />
        <Route path="/countries" element={<Countries />} />
        <Route path="/booking" element={<Booking />} />
        <Route path="/booking/:id" element={<Booking />} />
        <Route path="/booking/wizard/:serviceId" element={<ServiceBookingWizard />} />
        <Route path="/booking/fika/:creativeId" element={<QuickFikaBooking />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/add-role" element={<AddRole />} />
        <Route path="/register/creative" element={<AddRole />} />
        <Route path="/register/client" element={<AddRole />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }>
          <Route path="overview" element={<Overview />} />
          <Route path="client" element={<ClientDashboard />} />
          {/* Nested client routes for clearer URL alignment with breadcrumbs */}
          <Route path="client/bookings" element={<MyBookings />} />
          {/* Favorites - HIDDEN FOR LAUNCH
          <Route path="client/favorites" element={<Favorites />} />
          */}
          <Route path="client/orders" element={<Orders />} />
          <Route path="client/payments" element={<Payments />} />
          {/* Redirect legacy top-level routes to nested client paths */}
          <Route path="bookings" element={<Navigate to="/dashboard/client/bookings" replace />} />
          {/* <Route path="favorites" element={<Navigate to="/dashboard/client/favorites" replace />} /> */}
          {/* Legacy buyer/talent routes removed after migration */}
        {/* Creative dashboard routes */}
            <Route path="creative" element={<CreativeDashboard />} />
            {/* Route services and portfolio to the Talent wizard for full-featured forms */}
            <Route path="creative/services" element={<Creative />} />
            <Route path="creative/requests" element={<ServiceRequests />} />
            <Route path="creative/bookings" element={<MyBookings />} />
            <Route path="creative/earnings" element={<Earnings />} />
            <Route path="creative/portfolio" element={<Creative />} />
          <Route path="creative/messages" element={<Messages />} />
          <Route path="creative/orders" element={<Orders />} />
          <Route path="orders" element={<Orders />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="messages" element={<Messages />} />
          <Route path="users" element={<Users />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="support" element={<Support />} />
          <Route path="events" element={<MyEvents />} />
          <Route index element={<Overview />} />
          {/* Creative profile editor */}
          <Route path="creative/profile" element={<CreativeProfile />} />
        </Route>
        {/* LikesLog route removed per requirement */}
      </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
